<template>
  <div class="emergency-dossier-container p-3 p-md-4"> <!-- Add Bootstrap padding -->
    <h2 class="mb-4">{{ isEditing ? 'Modifier le Dossier Médical' : 'Nouveau Dossier Médical' }}</h2>
  
    <!-- ===== Informations Patient ===== -->
    <section class="form-section">
      <h3>Informations Patient</h3>
      <div class="row g-3"> <!-- Bootstrap row with gutters -->
        <div class="col-md-6"> <!-- Half width on medium screens and up -->
          <label for="nom" class="form-label">Nom du Patient</label>
          <input type="text" class="form-control form-control-sm" id="nom" v-model.trim="formState.patient.nom" placeholder="Nom et Prénom">
        </div>
        <div class="col-md-3 col-6"> <!-- Quarter width on medium, half on small -->
          <label for="age" class="form-label">Âge (ans)</label>
          <input type="number" class="form-control form-control-sm" id="age" v-model.number="formState.patient.age" min="0" max="120" placeholder="ex: 65">
        </div>
        <div class="col-md-3 col-6">
          <label class="form-label d-block">Sexe</label> <!-- d-block for spacing -->
          <div class="form-check form-check-inline">
            <input class="form-check-input" type="radio" v-model="formState.patient.sexe" value="masculin" id="sexeM">
            <label class="form-check-label" for="sexeM">M</label>
          </div>
          <div class="form-check form-check-inline">
            <input class="form-check-input" type="radio" v-model="formState.patient.sexe" value="féminin" id="sexeF">
            <label class="form-check-label" for="sexeF">F</label>
          </div>
        </div>
      </div>
    </section>
  
     <!-- ===== Motif de Consultation ===== -->
    <section class="form-section">
        <h3>Motif de Consultation</h3>
        <div class="mb-3">
          <label for="motif-consultation" class="form-label">Motif Principal</label>
           <div class="quick-motif-buttons mb-2">
              <!-- Keep buttons, maybe style with btn-sm btn-outline-secondary -->
              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="setMotif('Dyspnée aiguë')">Dyspnée aiguë</button>
              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="setMotif('Douleur thoracique')">Douleur thoracique</button>
              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="setMotif('Altération état de conscience')">Altération état de conscience</button>
              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="setMotif('Malaise / Syncope')">Malaise / Syncope</button>
              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="setMotif('Autre')">Altération de l'état neurologique</button>
              <!-- Add more -->
          </div>
          <input type="text" class="form-control form-control-sm" id="motif-consultation" v-model="formState.motifConsultation" placeholder="Cliquez ou décrivez...">
        </div>
    </section>
  
    <!-- ===== Évaluation de la Gravité (ABCDE) ===== -->
    <section class="form-section">
      <h3>Évaluation (ABCDE)</h3>
      <!-- === Respiratoire === -->
      <h4 class="section-h4">Respiratoire (A & B)</h4>
      <div class="row g-3 mb-3">
          <div class="col-md-6">
               <label class="form-label">Voies Aériennes</label>
               <div> <!-- Wrap radios -->
                   <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.resp.voies" value="Libres et sûres" id="vaLibres"><label class="form-check-label" for="vaLibres">Libres</label> </div>
                   <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.resp.voies" value="Obstruées" id="vaObstr"><label class="form-check-label" for="vaObstr">Obstruées</label> </div>
                   <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.resp.voies" value="Protégées" id="vaProt"><label class="form-check-label" for="vaProt">Protégées</label> </div>
                   <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.resp.voies" value="Inconnues" id="vaInc"><label class="form-check-label" for="vaInc">Inconnues</label> </div>
               </div>
          </div>
           <div class="col-md-6">
              <label class="form-label">Cyanose</label>
              <div>
                   <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.resp.cyanose" value="Oui" id="cyanOui"><label class="form-check-label" for="cyanOui">Oui</label> </div>
                   <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.resp.cyanose" value="Non" id="cyanNon"><label class="form-check-label" for="cyanNon">Non</label> </div>
              </div>
          </div>
      </div>
       <div class="row g-3 mb-3">
           <div class="col-md-6"> <label for="resp-spo2" class="form-label">SpO2 (%)</label> <input type="number" class="form-control form-control-sm" id="resp-spo2" v-model.number="formState.evaluation.resp.spo2" min="0" max="100" placeholder="ex: 95"> </div>
           <div class="col-md-6"> <label for="resp-fr" class="form-label">Fréq. Resp. (/min)</label> <input type="number" class="form-control form-control-sm" id="resp-fr" v-model.number="formState.evaluation.resp.fr" min="0" max="100" placeholder="ex: 16"> </div>
       </div>
       <div class="mb-3">
          <label class="form-label">Signes de Lutte</label>
           <div class="d-flex flex-wrap gap-3"> <!-- Use flex for check layout -->
              <div class="form-check"> <input class="form-check-input" type="checkbox" id="resp-tirage-present" v-model="formState.evaluation.resp.tirage.present"> <label class="form-check-label" for="resp-tirage-present"> Tirage </label> </div>
              <div class="form-check"> <input class="form-check-input" type="checkbox" id="resp-balancement" v-model="formState.evaluation.resp.balancement"> <label class="form-check-label" for="resp-balancement"> Balancement </label> </div>
              <div class="form-check"> <input class="form-check-input" type="checkbox" id="resp-wheezing" v-model="formState.evaluation.resp.wheezing"> <label class="form-check-label" for="resp-wheezing"> Wheezing </label> </div>
              <div class="form-check"> <input class="form-check-input" type="checkbox" id="resp-geignement" v-model="formState.evaluation.resp.geignement"> <label class="form-check-label" for="resp-geignement"> Geignement </label> </div>
           </div>
           <div v-if="formState.evaluation.resp.tirage.present" class="nested-details mt-2 p-2 border rounded bg-light">
               <label class="form-label mb-1 small">Localisation Tirage :</label>
               <div class="d-flex flex-wrap gap-3">
                   <div class="form-check"> <input class="form-check-input" type="checkbox" value="sus-sternal" v-model="formState.evaluation.resp.tirage.type" id="tirSus"><label class="form-check-label small" for="tirSus">Sus-sternal</label> </div>
                   <div class="form-check"> <input class="form-check-input" type="checkbox" value="intercostal" v-model="formState.evaluation.resp.tirage.type" id="tirInter"><label class="form-check-label small" for="tirInter">Intercostal</label> </div>
                   <div class="form-check"> <input class="form-check-input" type="checkbox" value="sous-costal" v-model="formState.evaluation.resp.tirage.type" id="tirSous"><label class="form-check-label small" for="tirSous">Sous-costal</label> </div>
                </div>
           </div>
       </div>
       <div class="mb-3"> <label for="resp-auscultation" class="form-label">Auscultation Pulmonaire</label> <input type="text" class="form-control form-control-sm" id="resp-auscultation" v-model="formState.evaluation.resp.auscultation" placeholder="ex: MVBS, râles crépitants..."> </div>
  
       <!-- === Hémodynamique === -->
       <h4 class="section-h4 mt-4">Hémodynamique (C)</h4>
       <!-- ... (Refactor Hemo section similarly using .row, .col-md-*, .mb-3, form-label, form-control, form-check) ... -->
        <div class="row g-3 mb-3">
            <div class="col-md-6"> <label for="hemo-ta" class="form-label">Pression Artérielle (mmHg)</label> <input type="text" class="form-control form-control-sm" id="hemo-ta" v-model="formState.evaluation.hemo.ta" placeholder="ex: 120/80"> </div>
            <div class="col-md-6"> <label for="hemo-fc" class="form-label">Fréq. Cardiaque (palpée - bpm)</label> <input type="number" class="form-control form-control-sm" id="hemo-fc" v-model.number="formState.evaluation.hemo.fc" min="0" max="300" placeholder="ex: 75"> </div>
        </div>
         <div class="row g-3 mb-3">
             <div class="col-md-6"> <label class="form-label">Régularité (Pouls Palpé)</label> <div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.hemo.rythmePalpe" value="Régulier" id="palpeReg"><label class="form-check-label" for="palpeReg">Régulier</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.hemo.rythmePalpe" value="Irrégulier" id="palpeIrreg"><label class="form-check-label" for="palpeIrreg">Irrégulier</label> </div> </div> </div>
             <div class="col-md-6"> <label class="form-label">Pouls Périphériques</label> <div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.hemo.pouls" value="Bien frappés" id="ppBF"><label class="form-check-label" for="ppBF">Bien Battant</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.hemo.pouls" value="Faibles" id="ppF"><label class="form-check-label" for="ppF">Faibles</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.hemo.pouls" value="Filants" id="ppFil"><label class="form-check-label" for="ppFil">Filants</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.hemo.pouls" value="Absents" id="ppAbs"><label class="form-check-label" for="ppAbs">Absents</label> </div> </div> </div>
        </div>
        <div class="row g-3 mb-3">
             <div class="col-md-6"> <label class="form-label">Extrémités</label> <div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.hemo.extremites" value="Roses et Chaudes" id="extRC"><label class="form-check-label" for="extRC">Roses/Chaudes</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.hemo.extremites" value="Pâles" id="extPale"><label class="form-check-label" for="extPale">Pâles</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.hemo.extremites" value="Froides" id="extFroide"><label class="form-check-label" for="extFroide">Froides</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.hemo.extremites" value="Marbrées" id="extMarb"><label class="form-check-label" for="extMarb">Marbrées</label> </div> </div> </div>
             <div class="col-md-6"> <label for="hemo-trc" class="form-label">TRC (s)</label> <input type="number" class="form-control form-control-sm" id="hemo-trc" v-model.number="formState.evaluation.hemo.trc" min="0" max="20" step="0.5" placeholder="ex: < 3"> </div>
         </div>
         <div class="mb-3"> <label class="form-label">Signes ICD</label> <div class="d-flex flex-wrap gap-3"> <div class="form-check"> <input class="form-check-input" type="checkbox" id="hemo-jugulaires" v-model="formState.evaluation.hemo.signesICD.turgescenceJug"> <label class="form-check-label" for="hemo-jugulaires"> Turgescence Jug. </label> </div> <div class="form-check"> <input class="form-check-input" type="checkbox" id="hemo-reflux" v-model="formState.evaluation.hemo.signesICD.refluxHepatoJug"> <label class="form-check-label" for="hemo-reflux"> RHJ </label> </div> <div class="form-check"> <input class="form-check-input" type="checkbox" id="hemo-oedemes" v-model="formState.evaluation.hemo.signesICD.oedemesMI"> <label class="form-check-label" for="hemo-oedemes"> OMI </label> </div> </div> </div>
         <!-- ECG Section -->
         <div class="ecg-section border rounded p-3 mb-3 bg-light">
             <div class="form-check mb-2"> <input class="form-check-input" type="checkbox" id="ecg-realise" v-model="formState.evaluation.hemo.ecg.realise"> <label class="form-check-label fw-bold" for="ecg-realise"> ECG Réalisé </label> </div>
             <template v-if="formState.evaluation.hemo.ecg.realise">
                 <div class="row g-3 mb-2">
                     <div class="col-md-4"> <label for="ecg-rythmeType" class="form-label small">Rythme</label> <select class="form-select form-select-sm" id="ecg-rythmeType" v-model="formState.evaluation.hemo.ecg.rythmeType"> <option>Sinusal</option><option>FA</option><option>Flutter</option><option>TSV</option><option>TV</option><option>FV</option><option>BAV</option><option>ESSV</option><option>ESV</option><option>Jonctionnel</option><option>Idioventricul</option><option>Asystolie</option><option>Paced</option><option>Autre</option><option>Ininterprétable</option> </select> </div>
                     <div class="col-md-4"> <label for="ecg-fc" class="form-label small">FC (ECG)</label> <input type="number" class="form-control form-control-sm" id="ecg-fc" v-model.number="formState.evaluation.hemo.ecg.fc" min="0" max="300" placeholder="bpm"> </div>
                     <div class="col-md-4"> <label class="form-label small d-block">Régularité</label> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="Régulier" v-model="formState.evaluation.hemo.ecg.regularite" id="ecgReg"><label class="form-check-label small" for="ecgReg">Rég.</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="Irrégulier" v-model="formState.evaluation.hemo.ecg.regularite" id="ecgIrreg"><label class="form-check-label small" for="ecgIrreg">Irrég.</label> </div> </div>
                 </div>
                 <div class="row g-3">
                     <div class="col-md-6"> <label for="ecg-stAnomalies" class="form-label small">Anomalies ST/T</label> <select class="form-select form-select-sm" id="ecg-stAnomalies" v-model="formState.evaluation.hemo.ecg.stAnomalies"> <option>Normal</option><option>Sus-ST</option><option>Sous-ST</option><option>T nég.</option><option>T pointues</option><option>T plates</option><option>Onde Q</option><option>Autre</option> </select> </div>
                     <div class="col-md-6" v-if="formState.evaluation.hemo.ecg.stAnomalies !== 'Normal' && formState.evaluation.hemo.ecg.stAnomalies !== 'Non évaluable'"> <label for="ecg-stLocalisation" class="form-label small">Localisation</label> <input type="text" class="form-control form-control-sm" id="ecg-stLocalisation" v-model="formState.evaluation.hemo.ecg.stLocalisation" placeholder="ex: V1-V4, inf..."> </div>
                 </div>
                 <div class="mt-2 p-2 border-top small text-muted"> Interprétation Auto: <i>{{ formState.evaluation.hemo.ecg.interpretation || "Non générée." }}</i> </div>
             </template>
         </div>
  
       <!-- === Neurologique === -->
       <h4 class="section-h4 mt-4">Neurologique (D)</h4>
        <div class="mb-3 p-3 border rounded bg-light"> <!-- GCS Section -->
          <label class="form-label fw-bold">Score de Glasgow: <strong>{{ calculatedGCS }} / 15</strong></label>
          <div class="mb-2"> <label :for="'neuro-yeux'+_uid" class="form-label small">Ouverture Yeux (Y): {{ formState.evaluation.neuro.yeux }} / 4 <span class="text-muted fst-italic">({{ gcsYeuxText }})</span></label> <input type="range" class="form-range" :id="'neuro-yeux'+_uid" v-model.number="formState.evaluation.neuro.yeux" min="1" max="4" step="1"> </div>
          <div class="mb-2"> <label :for="'neuro-verbal'+_uid" class="form-label small">Réponse Verbale (V): {{ formState.evaluation.neuro.verbal }} / 5 <span class="text-muted fst-italic">({{ gcsVerbalText }})</span></label> <input type="range" class="form-range" :id="'neuro-verbal'+_uid" v-model.number="formState.evaluation.neuro.verbal" min="1" max="5" step="1"> </div>
          <div> <label :for="'neuro-moteur'+_uid" class="form-label small">Réponse Motrice (M): {{ formState.evaluation.neuro.moteur }} / 6 <span class="text-muted fst-italic">({{ gcsMoteurText }})</span></label> <input type="range" class="form-range" :id="'neuro-moteur'+_uid" v-model.number="formState.evaluation.neuro.moteur" min="1" max="6" step="1"> </div>
       </div>
        <div class="row g-3 mb-3">
           <div class="col-md-6"> <label class="form-label">Orientation</label> <div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.neuro.orientation" :value="`Bien orienté${accordGenre} temps et espace`" id="orientOK"><label class="form-check-label" :for="`orientOK${_uid}`">Orienté T/E</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.neuro.orientation" :value="`Confus${accordGenre}`" id="orientConfus"><label class="form-check-label" :for="`orientConfus${_uid}`">Confus</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.neuro.orientation" :value="`Désorienté${accordGenre}`" id="orientDes"><label class="form-check-label" :for="`orientDes${_uid}`">Désorienté</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.neuro.orientation" :value="`Non réactif${accordGenre ? 've' : ''}`" id="orientNR"><label class="form-check-label" :for="`orientNR${_uid}`">Non réactif</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.evaluation.neuro.orientation" :value="`Non évaluable`" id="orientNE"><label class="form-check-label" :for="`orientNE${_uid}`">N/E</label> </div> </div> </div>
           <div class="col-md-6"> <label for="neuro-pupilles" class="form-label">Pupilles</label> <input type="text" class="form-control form-control-sm" id="neuro-pupilles" v-model="formState.evaluation.neuro.pupilles" placeholder="ex: égales, réactives"> </div>
        </div>
       <div class="mb-3">
          <label class="form-label">Déficit Focal</label>
          <div class="d-flex flex-wrap gap-3">
             <div class="form-check"> <input class="form-check-input" type="checkbox" id="neuro-deficit-present" v-model="formState.evaluation.neuro.deficitFocal.present"> <label class="form-check-label" for="neuro-deficit-present"> Déficit Présent </label> </div>
             <div class="form-check"> <input class="form-check-input" type="checkbox" id="neuro-aphasie" v-model="formState.evaluation.neuro.deficitFocal.aphasie"> <label class="form-check-label" for="neuro-aphasie"> Aphasie/Dysarthrie </label> </div>
          </div>
          <div v-if="formState.evaluation.neuro.deficitFocal.present" class="nested-details mt-2 p-2 border rounded bg-light">
               <label class="form-label mb-1 small">Détails Déficit :</label>
               <div class="mb-2"> <label class="form-label small d-block">Côté</label> <div class="d-flex flex-wrap gap-3"> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="Droit" v-model="formState.evaluation.neuro.deficitFocal.cote" id="defD"><label class="form-check-label small" for="defD">D</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="Gauche" v-model="formState.evaluation.neuro.deficitFocal.cote" id="defG"><label class="form-check-label small" for="defG">G</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="Bilatéral" v-model="formState.evaluation.neuro.deficitFocal.cote" id="defB"><label class="form-check-label small" for="defB">Bilat.</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="Non latéralisé" v-model="formState.evaluation.neuro.deficitFocal.cote" id="defNL"><label class="form-check-label small" for="defNL">N/L</label> </div> </div> </div>
               <div class="row g-2">
                  <div class="col-md-6"> <label for="deficit-moteur-desc" class="form-label small">Moteur</label> <input type="text" class="form-control form-control-sm" id="deficit-moteur-desc" v-model="formState.evaluation.neuro.deficitFocal.moteur" placeholder="ex: Hémiparésie..."> </div>
                   <div class="col-md-6"> <label for="deficit-sensitif-desc" class="form-label small">Sensitif</label> <input type="text" class="form-control form-control-sm" id="deficit-sensitif-desc" v-model="formState.evaluation.neuro.deficitFocal.sensitif" placeholder="ex: Hémianesthésie..."> </div>
               </div>
          </div>
       </div>
       <div v-if="calculatedGCS < 15" class="detailed-neuro-exam border rounded p-3 mb-3 bg-light">
           <h5 class="h6 mb-2">Examen Neuro Détaillé</h5>
            <div class="mb-2"> <label class="form-label small">Signes Méningés</label> <div class="d-flex flex-wrap gap-3"> <div class="form-check"> <input class="form-check-input" type="checkbox" v-model="formState.evaluation.neuroDetaille.nuqueRaide" id="nr"><label class="form-check-label small" for="nr">Nuque Raide</label> </div> <div class="form-check"> <input class="form-check-input" type="checkbox" v-model="formState.evaluation.neuroDetaille.kernig" id="nk"><label class="form-check-label small" for="nk">Kernig</label> </div> <div class="form-check"> <input class="form-check-input" type="checkbox" v-model="formState.evaluation.neuroDetaille.brudzinski" id="nb"><label class="form-check-label small" for="nb">Brudzinski</label> </div> </div> </div>
             <div class="row g-2 mb-2">
               <div class="col-md-6"> <label for="neuro-rots" class="form-label small">ROT</label> <input type="text" class="form-control form-control-sm" id="neuro-rots" v-model="formState.evaluation.neuroDetaille.rots" placeholder="ex: Vifs, sym..."> </div>
                <div class="col-md-6"> <label class="form-label small">RCP</label> <div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="Indifférent" v-model="formState.evaluation.neuroDetaille.rcp" id="rcpI"><label class="form-check-label small" for="rcpI">Indiff.</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="Flexion" v-model="formState.evaluation.neuroDetaille.rcp" id="rcpF"><label class="form-check-label small" for="rcpF">Flex.</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="Extension (Babinski)" v-model="formState.evaluation.neuroDetaille.rcp" id="rcpE"><label class="form-check-label small" for="rcpE">Ext.</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="Non obtenu" v-model="formState.evaluation.neuroDetaille.rcp" id="rcpN"><label class="form-check-label small" for="rcpN">N/O</label> </div> </div> </div>
             </div>
              <div> <label for="neuro-autres" class="form-label small">Autres Signes</label> <input type="text" class="form-control form-control-sm" id="neuro-autres" v-model="formState.evaluation.neuroDetaille.autresSignes" placeholder="Mvts anormaux, tonus..."> </div>
       </div>
  
       <!-- === Exposition / Examen Complémentaire === -->
       <h4 class="section-h4 mt-4">Exposition / Environnement (E)</h4>
        <div class="row g-3 mb-3">
           <div class="col-md-6"> <label for="examen-temp" class="form-label">Température (°C)</label> <input type="number" step="0.1" class="form-control form-control-sm" id="examen-temp" v-model.number="formState.evaluation.examenComp.temp" min="30" max="45" placeholder="ex: 37.5"> </div>
           <div class="col-md-6"> <label for="examen-glycemie" class="form-label">Glycémie Capillaire</label> <input type="text" class="form-control form-control-sm" id="examen-glycemie" v-model="formState.evaluation.examenComp.glycemie" placeholder="ex: 1.10 g/L"> </div>
         </div>
         <div class="mb-3"> <label class="form-label">Autres Constatations</label> <div class="d-flex flex-wrap gap-3"> <div class="form-check"> <input class="form-check-input" type="checkbox" id="examen-deshydratation" v-model="formState.evaluation.examenComp.signesDeshydratation"> <label class="form-check-label" for="examen-deshydratation"> Signes Déshydratation </label> </div> <div class="form-check"> <input class="form-check-input" type="checkbox" id="examen-lesions" v-model="formState.evaluation.examenComp.lesionsTraumatiques"> <label class="form-check-label" for="examen-lesions"> Lésions Traumatiques </label> </div> </div> </div>
        <div class="mb-3"> <label for="examen-abdomen" class="form-label">Examen Abdominal</label> <textarea class="form-control form-control-sm" id="examen-abdomen" v-model="formState.evaluation.examenComp.abdomen" rows="2" placeholder="Souple, dépressible..."></textarea> </div>
        <div class="mb-3"> <label for="examen-tr" class="form-label">Toucher Rectal (si pertinent)</label> <textarea class="form-control form-control-sm" id="examen-tr" v-model="formState.evaluation.examenComp.tr" rows="1" placeholder="Doigtier propre..."></textarea> </div>
    </section>
     <!-- *** NEW: Clinical Scores Section *** -->
     <section class="form-section">
     <h4 class="section-h4 mt-4">Scores Cliniques</h4>
        <div class="row g-3">

            <!-- qSOFA -->
            <div class="col-md-4">
                <div class="card h-100">
                    <div class="card-header bg-light py-2"><h5 class="h6 mb-0">qSOFA</h5></div>
                    <div class="card-body p-2">
                         <div class="form-check mb-1">
                             <input class="form-check-input" type="checkbox" v-model="formState.evaluation.scores.qsofa.respiratoryRate" id="qsofaRR">
                             <label class="form-check-label small" for="qsofaRR">FR ≥ 22/min</label>
                         </div>
                          <div class="form-check mb-1">
                              <!-- Disabled as it's derived from GCS -->
                             <input class="form-check-input" type="checkbox" :checked="formState.evaluation.scores.qsofa.alteredMentalStatus" id="qsofaAMS" disabled>
                             <label class="form-check-label small" for="qsofaAMS">Altération Conscience (GCS<15)</label>
                         </div>
                          <div class="form-check">
                             <input class="form-check-input" type="checkbox" v-model="formState.evaluation.scores.qsofa.systolicBP" id="qsofaSBP">
                             <label class="form-check-label small" for="qsofaSBP">PAS ≤ 100 mmHg</label>
                         </div>
                         <div class="mt-2 pt-2 border-top">
                             <strong class="small">Score qSOFA: {{ computedQSOFAScore ?? '...' }}</strong>
                         </div>
                    </div>
                </div>
            </div>

            <!-- CURB-65 -->
             <div class="col-md-4">
                <div class="card h-100">
                    <div class="card-header bg-light py-2"><h5 class="h6 mb-0">CURB-65</h5></div>
                    <div class="card-body p-2">
                         <div class="form-check mb-1">
                             <input class="form-check-input" type="checkbox" v-model="formState.evaluation.scores.curb65.confusion" id="curbC">
                             <label class="form-check-label small" for="curbC">Confusion</label>
                         </div>
                         <div class="form-check mb-1">
                             <input class="form-check-input" type="checkbox" v-model="formState.evaluation.scores.curb65.urea" id="curbU">
                             <label class="form-check-label small" for="curbU">Urée > 7mmol/L</label>
                             <!-- Or input: <input type="number" v-model...> -->
                         </div>
                         <div class="form-check mb-1">
                             <input class="form-check-input" type="checkbox" v-model="formState.evaluation.scores.curb65.respiratoryRate" id="curbR">
                             <label class="form-check-label small" for="curbR">FR ≥ 30/min</label>
                         </div>
                         <div class="form-check mb-1">
                             <input class="form-check-input" type="checkbox" v-model="formState.evaluation.scores.curb65.bloodPressure" id="curbB">
                             <label class="form-check-label small" for="curbB">PAS<90 ou PAD≤60</label>
                         </div>
                         <div class="form-check mb-1">
                              <!-- Disabled as it's derived from age -->
                             <input class="form-check-input" type="checkbox" :checked="formState.evaluation.scores.curb65.age65" id="curbA" disabled>
                             <label class="form-check-label small" for="curbA">Âge ≥ 65 ans</label>
                         </div>
                          <div class="mt-2 pt-2 border-top">
                             <strong class="small">Score CURB-65: {{ computedCurb65Score ?? '...' }}</strong>
                         </div>
                    </div>
                </div>
            </div>

             <!-- HEART Score -->
             <div class="col-md-4">
                <div class="card h-100">
                    <div class="card-header bg-light py-2"><h5 class="h6 mb-0">HEART Score</h5></div>
                    <div class="card-body p-2">
                        <div class="mb-2">
                            <label class="form-label small mb-1">Histoire</label>
                            <select class="form-select form-select-sm" v-model.number="formState.evaluation.scores.heart.history">
                                <option :value="null" disabled>Choisir...</option> <option value="0">Peu suspecte</option> <option value="1">Mod. suspecte</option> <option value="2">Très suspecte</option>
                            </select>
                        </div>
                         <div class="mb-2">
                            <label class="form-label small mb-1">ECG</label>
                            <select class="form-select form-select-sm" v-model.number="formState.evaluation.scores.heart.ecg">
                                <option :value="null" disabled>Choisir...</option> <option value="0">Normal</option> <option value="1">Non spécif.</option> <option value="2">Sus-décalage ST signif.</option>
                            </select>
                        </div>
                         <div class="mb-2">
                            <label class="form-label small mb-1">Âge</label>
                            <select class="form-select form-select-sm" v-model.number="formState.evaluation.scores.heart.age">
                                <option :value="null" disabled>Choisir...</option> <option value="0">< 45</option> <option value="1">45-64</option> <option value="2">≥ 65</option>
                            </select>
                        </div>
                         <div class="mb-2">
                            <label class="form-label small mb-1">Facteurs de Risque</label>
                            <select class="form-select form-select-sm" v-model.number="formState.evaluation.scores.heart.riskFactors">
                                <option :value="null" disabled>Choisir...</option> <option value="0">Aucun</option> <option value="1">1-2 FDR</option> <option value="2">≥ 3 FDR ou Athérosclérose</option>
                            </select>
                        </div>
                         <div class="mb-2">
                            <label class="form-label small mb-1">Troponine Initiale</label>
                             <select class="form-select form-select-sm" v-model.number="formState.evaluation.scores.heart.troponin">
                                <option :value="null" disabled>Choisir...</option> <option value="0">≤ Limite Normale</option> <option value="1">1-3x Normale</option> <option value="2">> 3x Normale</option>
                            </select>
                        </div>
                         <div class="mt-2 pt-2 border-top">
                             <strong class="small">Score HEART: {{ computedHeartScore ?? '...' }}</strong>
                         </div>
                    </div>
                </div>
            </div>

        </div> <!-- End row -->
    </section> <!-- End Scores Section -->
    <!-- ===== Synthèse / Conduite à Tenir ===== -->
     <section class="form-section">
       <h3>Synthèse & CAT</h3>
       <div class="mb-3">
           <label class="form-label">Détresse Identifiée</label>
           <div>
               <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="aucune" v-model="formState.evaluation.distressIdentifiee" id="distAucune"><label class="form-check-label" for="distAucune">Aucune</label> </div>
               <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="respiratoire" v-model="formState.evaluation.distressIdentifiee" id="distResp"><label class="form-check-label" for="distResp">Respiratoire</label> </div>
               <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="hemodynamique" v-model="formState.evaluation.distressIdentifiee" id="distHemo"><label class="form-check-label" for="distHemo">Hémodynamique</label> </div>
               <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" value="neurologique" v-model="formState.evaluation.distressIdentifiee" id="distNeuro"><label class="form-check-label" for="distNeuro">Neurologique</label> </div>
           </div>
       </div>
       <div class="mb-3"> <label for="synthese" class="form-label">Synthèse / Impression Clinique</label> <textarea class="form-control" id="synthese" v-model="formState.synthese" rows="4" placeholder="Détails de l'impression clinique..."></textarea> </div>
       <h4 class="section-h4">Conduite à Tenir</h4>
        <div class="mb-3"> <div class="d-flex flex-wrap gap-3"> <div class="form-check"> <input class="form-check-input" type="checkbox" id="conduite-hospit" v-model="formState.conduite.hospit"> <label class="form-check-label" for="conduite-hospit"> Hospitalisation </label> </div> <div class="form-check"> <input class="form-check-input" type="checkbox" id="conduite-vvp" v-model="formState.conduite.vvp"> <label class="form-check-label" for="conduite-vvp"> VVP </label> </div> <div class="form-check"> <input class="form-check-input" type="checkbox" id="conduite-monitor" v-model="formState.conduite.monitor"> <label class="form-check-label" for="conduite-monitor"> Monitoring </label> </div> </div> </div>
         <div class="mb-3"> <label for="conduite-o2" class="form-label">Oxygénothérapie (L/min)</label> <input type="number" class="form-control form-control-sm" id="conduite-o2" v-model.number="formState.conduite.o2" min="0" max="15" placeholder="L/min si besoin"> </div>
         <div class="mb-3"> <label for="conduite-traitements" class="form-label">Traitements / Prescriptions</label> <textarea class="form-control" id="conduite-traitements" v-model="formState.conduite.traitements" rows="3" placeholder="ex: Aspirine, Antalgiques, Bilan, Imagerie..."></textarea> </div>
     </section>
  
    <!-- ===== Terrain / Antécédents ===== -->
    <section class="form-section">
        <h3>Terrain & Antécédents</h3>
        <div class="antecedents-list mb-3">
          <div v-for="(antecedent, index) in formState.terrain.antecedents" :key="antecedent.id || index" class="antecedent-item p-2 mb-2">
              <div class="antecedent-toggle form-check">
                   <input class="form-check-input" type="checkbox" :id="'atcd-present-' + index" v-model="antecedent.present">
                   <label class="form-check-label fw-bold" :for="'atcd-present-' + index">{{ antecedent.name }}</label>
              </div>
              <div class="antecedent-details mt-1" v-if="antecedent.present">
                   <input type="text" class="form-control form-control-sm" :id="'atcd-details-' + index" v-model="antecedent.details" :placeholder="`Détails/Ttt...`">
              </div>
          </div>
        </div>
        <div class="add-antecedent-form mb-3">
            <label for="new-antecedent-name" class="form-label small">Ajouter un antécédent :</label>
            <div class="input-group input-group-sm">
                <input type="text" class="form-control" id="new-antecedent-name" v-model="formState.terrain.newAntecedentName" @keyup.enter="addAntecedent" placeholder="Nom...">
                <button type="button" @click="addAntecedent" class="btn btn-outline-secondary">+</button>
            </div>
        </div>
         <div class="mb-3"> <label for="terrain-allergies" class="form-label">Allergies</label> <input type="text" class="form-control form-control-sm" id="terrain-allergies" v-model="formState.terrain.allergies" placeholder="ex: Pénicilline (rash)..."> </div>
         <div class="mb-3"> <label for="terrain-traitements" class="form-label">Traitements Habituels (autres)</label> <textarea class="form-control form-control-sm" id="terrain-traitements" v-model="formState.terrain.traitementsHabituels" rows="2" placeholder="Lister autres Ttt"></textarea> </div>
         <div class="row g-3">
             <div class="col-md-6"> <label class="form-label">Tabac</label> <div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.terrain.tabac.type" value="Fumeur actif" id="tabacActif"><label class="form-check-label" for="tabacActif">Actif</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.terrain.tabac.type" value="Ancien fumeur" id="tabacAncien"><label class="form-check-label" for="tabacAncien">Ancien</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.terrain.tabac.type" value="Non fumeur" id="tabacNon"><label class="form-check-label" for="tabacNon">Jamais</label> </div> </div> <input class="form-control form-control-sm mt-1" v-if="formState.terrain.tabac.type !== 'Non fumeur'" type="text" v-model="formState.terrain.tabac.details" placeholder="PA ex: 30"> </div>
             <div class="col-md-6"> <label class="form-label">Alcool</label> <div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.terrain.alcool.type" value="Consommation régulière" id="alcReg"><label class="form-check-label" for="alcReg">Régul.</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.terrain.alcool.type" value="Consommation occasionnelle" id="alcOcc"><label class="form-check-label" for="alcOcc">Occas.</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.terrain.alcool.type" value="Abstinent" id="alcAbs"><label class="form-check-label" for="alcAbs">Abstinent</label> </div> <div class="form-check form-check-inline"> <input class="form-check-input" type="radio" v-model="formState.terrain.alcool.type" value="Sevrage récent" id="alcSev"><label class="form-check-label" for="alcSev">Sevrage</label> </div> </div> <input class="form-control form-control-sm mt-1" v-if="formState.terrain.alcool.type.includes('Consommation') || formState.terrain.alcool.type === 'Sevrage récent'" type="text" v-model="formState.terrain.alcool.details" placeholder="Unités/semaine..."> </div>
         </div>
     </section>
  
     <!-- ===== Histoire de la Maladie ===== -->
     <section class="form-section">
       <h3>Histoire de la Maladie</h3>
       <div class="mb-3"> <label for="histoire" class="form-label">Anamnèse</label> <textarea class="form-control" id="histoire" v-model="formState.histoire" rows="6" placeholder="Circonstances, symptômes, chronologie..."></textarea> </div>
     </section>
  
    <!-- ===== Affichage Dossier Généré & Actions ===== -->
    <section class="form-section dossier-genere">
      <div class="dossier-header">
          <h3>📄 {{ isEditing ? 'Aperçu (Édition)' : 'Aperçu Dossier Généré' }}</h3>
          <div class="dossier-actions">
            <button v-if="isEditing" @click="cancelEdit" type="button" class="btn btn-secondary btn-sm" title="Annuler les modifications">
                 <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 384 512" fill="currentColor"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg> <!-- Example X icon -->
                 Annuler
             </button>
             
             <button @click="triggerSaveOrUpdateDossier" :disabled="saveLoading" class="btn btn-success btn-sm" :title="isEditing ? 'Enregistrer modifications' : 'Enregistrer nouveau'">
                <span v-if="saveLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                <svg v-else xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 448 512" fill="currentColor"><path d="M433.9 129.9l-83.9-83.9A48 48 0 0 0 316.1 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V163.9a48 48 0 0 0 -14.1-33.9zM224 416c-35.3 0-64-28.7-64-64s28.7-64 64-64s64 28.7 64 64s-28.7 64-64 64zm96-304.5H96V128h224v83.5c0 13.3 10.7 24 24 24H384v80.5c0 17.8-7.8 33.5-20.2 44.4l-1.3 1.1c-14.6 12.6-33.1 20-52.5 20c-48.6 0-88-39.4-88-88s39.4-88 88-88c17.8 0 34.5 5.3 48.8 14.5l-1.3-1.1L384 112.5v-17z"/></svg> <!-- Example Save icon -->
                 {{ isEditing ? 'Mettre à Jour' : 'Enregistrer' }}
             </button>
             <button @click="copyDossierToClipboard" class="btn btn-outline-secondary btn-sm" title="Copier texte">
                <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 448 512"><path fill="currentColor" d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16l140.1 0L400 115.9V320c0 8.8-7.2 16-16 16zM192 384H384c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V416H272v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16H192V128H64z"/></svg> <!-- Example Copy icon -->
                Copier
             </button>     
          </div>
        </div>
        <pre class="output bg-light border rounded p-3">{{ generateDossier() }}</pre>
         <p v-if="saveError" class="error-message mt-2">{{ saveError }}</p>
    </section>
  
  </div>
  </template>
  
  <script setup>
  import { ref, reactive, computed, watch, onMounted, nextTick, getCurrentInstance } from 'vue';
  import { supabase } from '../../supabase.js'; // Ensure path is correct
  
// --- Props & Emits ---
const props = defineProps({
    editingDossier: { type: Object, default: null }
});
const emit = defineEmits(['dossier-saved', 'dossier-updated', 'cancel-edit']);

// --- Initial State ---
const getInitialFormData = (sexe = 'masculin') => {
    const genreSuffix = sexe === 'féminin' ? 'e' : '';
    // Define the complete initial structure
    return {
        patient: { nom: '', age: null, sexe: sexe },
        motifConsultation: '',
        evaluation: {
            resp: { voies: 'Libres et sûres', spo2: null, fr: null, auscultation: '', tirage: { present: false, type: [] }, balancement: false, wheezing: false, geignement: false, cyanose: 'Non' },
            hemo: { ta: '', fc: null, rythmePalpe: 'Régulier', extremites: 'Chaudes', trc: null, pouls: 'Présents et symétriques', signesICD: { turgescenceJug: false, refluxHepatoJug: false, oedemesMI: false }, ecg: { realise: false, rythmeType: 'Non réalisé', regularite: 'Régulier', stAnomalies: 'Normal', stLocalisation: '', interpretation: '' } },
            neuro: { yeux: 4, verbal: 5, moteur: 6, orientation: `Bien orienté${genreSuffix} temps et espace`, pupilles: '', deficitFocal: { present: false, cote: '', moteur: '', sensitif: '', aphasie: false } },
            neuroDetaille: { nuqueRaide: false, kernig: false, brudzinski: false, rots: '', rcp: 'Indifférent', autresSignes: '' },
            examenComp: { temp: null, glycemie: '', signesDeshydratation: false, lesionsTraumatiques: false, abdomen: '', tr: '' },
            distressIdentifiee: 'aucune',
            scores: {
                heart: {
                    history: null, // 0, 1, 2
                    ecg: null,     // 0, 1, 2
                    age: null,      // 0, 1, 2
                    riskFactors: null, // 0, 1, 2
                    troponin: null, // 0, 1, 2
                    result: null   // Calculated score
                },
                curb65: {
                    confusion: false, // true/false
                    urea: null,       // BUN or Urea level (or checkbox for > threshold)
                    respiratoryRate: null, // RR (or checkbox for >=30)
                    bloodPressure: null,   // SBP/DBP (or checkbox for SBP<90 or DBP<=60)
                    age65: false,     // true/false (can be derived from patient.age)
                    result: null      // Calculated score
                },
                qsofa: {
                    respiratoryRate: null, // RR >= 22
                    alteredMentalStatus: false, // GCS < 15
                    systolicBP: null, // SBP <= 100
                    result: null // Calculated score
                }
             },
        },
        synthese: '',
        conduite: { hospit: true, o2: null, vvp: true, monitor: true, traitements: '' },
        terrain: {
            antecedents: [ { id: 'hta', name: 'HTA', present: false, details: '' }, { id: 'diabete', name: 'Diabète', present: false, details: '' }, { id: 'cardio', name: 'Coronaropathie / Cardiopathie', present: false, details: '' }, { id: 'resp', name: 'BPCO / Asthme', present: false, details: '' }, { id: 'irc', name: 'Insuffisance Rénale Chronique', present: false, details: '' }, { id: 'avc', name: 'AVC / AIT', present: false, details: '' }, { id: 'goutte', name: 'Goutte', present: false, details: '' }, ],
            newAntecedentName: '', allergies: '', traitementsHabituels: '',
            tabac: { type: 'Non fumeur', details: '' }, alcool: { type: 'Abstinent', details: '' },
        },
        histoire: '',
        
        }
        
        
};



// --- Reactive State ---
const formState = reactive(getInitialFormData()); // Initialize with default structure
const isEditing = ref(false);
const currentDossierId = ref(null);
const saveLoading = ref(false);
const saveError = ref('');

// Unique ID for GCS range inputs
const instance = getCurrentInstance();
const _uid = instance ? instance.uid : Date.now();

// --- Formatting Helpers & Core Logic Methods DECLARED FIRST ---
const _formatVal = (val, unit = '', defaultText = '_____') => {
    if (val !== null && val !== undefined && val !== '') { return `${val}${unit}`; }
    return defaultText;
};

// Logic is: Checkbox checked = TRUE = Signe PRÉSENT
const _formatPresenceAbsence = (val, textSiPresent, textSiAbsentPrefix = 'Pas de ') => {
    if (val === true) { // If the sign is present (checkbox checked)
        return textSiPresent;
    } else if (val === false) { // If the sign is absent (checkbox unchecked)
        const prefix = textSiPresent.toLowerCase().startsWith('o') || textSiPresent.toLowerCase().startsWith('œ') ? "Pas d'" : textSiAbsentPrefix;
        return `${prefix}${textSiPresent}`;
    }
    // If val is not boolean (e.g., initial state before interaction)
    return `${textSiPresent} : N/E`; // N/E for Not Evaluated
};

const _formatSignesLutte = () => {
    // pas de signes de lutte 
    if (!formState.evaluation.resp.wheezing && !formState.evaluation.resp.geignement && !formState.evaluation.resp.tirage.present && !formState.evaluation.resp.balancement) return '- Pas de signes de lutte respiratoire';
    else {
        let output = '- Tirage : ' + (formState.evaluation.resp.tirage.present ? ` (${formState.evaluation.resp.tirage.type.join('/')||'non précisé'})` : 'Absent') + '\n';
        if (formState.evaluation.resp.balancement) output += '  - Balancement thoracoabdominal \n';
        else
            output += '  ';
    
    // Add wheezing/geignement if needed in output
        if(formState.evaluation.resp.wheezing) output += '\n  - Wheezing audible à l\'auscultation';
        if(formState.evaluation.resp.geignement) output += '\n  - Geignement expiratoire: Oui';
        return output;
    }
    
};

const _formatNeuroDeficits = () => {
    const focal = formState.evaluation.neuro.deficitFocal;
    if (!focal.present && !focal.aphasie) return 'Pas de déficit sensitivo-moteur ou aphasie';
    let desc = [];
    if (focal.present) {
        let details = [];
        if (focal.cote) details.push(focal.cote);
        if (focal.moteur) details.push(`moteur: ${focal.moteur}`);
        if (focal.sensitif) details.push(`sensitif: ${focal.sensitif}`);
        if (details.length > 0) desc.push(`Déficit focal (${details.join(', ')})`);
        else desc.push('Déficit focal présent (non détaillé)');
    }
    if (focal.aphasie) {
        desc.push('Aphasie / Dysarthrie présente');
    }
    return desc.join(' | ');
};

const _formatConduite = () => {
    let conduiteTxt = '';
    if (formState.conduite.hospit) conduiteTxt += '- Hospitalisation\n';
    if (formState.conduite.o2 !== null && formState.conduite.o2 >= 0) conduiteTxt += `- Oxygénothérapie : ${formState.conduite.o2 === 0 ? 'Non' : _formatVal(formState.conduite.o2, ' L/min au MFS')}\n`;
    else conduiteTxt += ' '; // Or 'Non' if 0 means non
    if (formState.conduite.vvp) conduiteTxt += '- Mise en place de voie(s) veineuse(s) périphérique(s)\n';
    if (formState.conduite.monitor) conduiteTxt += '- Monitorage continu : TA, FC, FR, SpO2, ECG\n';
    if (formState.conduite.traitements) conduiteTxt += `- Traitements/Prescr.: ${formState.conduite.traitements}\n`;
    return conduiteTxt.trim() || "- Aucune mesure immédiate spécifique renseignée.";
};

const _formatTerrain = () => {
    let terrainTxt = '';
    // Dynamic ATCDs
    const atcdsPresents = formState.terrain.antecedents
                            .filter(atcd => atcd.present)
                            .map(atcd => `- ${atcd.name}${atcd.details ? ' ' + atcd.details + '' : ''}`);
    if (atcdsPresents.length > 0) {
        terrainTxt += atcdsPresents.join('\n') + '\n';
    } else {
        terrainTxt += "- Aucun antécédent notable renseigné\n";
    }

    // Tabac
    let tabacDetail = formState.terrain.tabac.details ? ` (${_formatVal(formState.terrain.tabac.details, ' paquets/année', '_____')})` : '';
    if (formState.terrain.tabac.type !== 'Non fumeur' && !formState.terrain.tabac.details) tabacDetail = ' (_____ paquets/année)'; // Force placeholder if type != non fumeur and vide
    switch (formState.terrain.tabac.type) {
        case 'Fumeur actif': terrainTxt += `- Tabac : Fumeur actif${tabacDetail}\n`; break;
        case 'Ancien fumeur': terrainTxt += `- Tabac : Ancien fumeur${tabacDetail}\n`; break;
        case 'Non fumeur': terrainTxt += `- Tabac : Non fumeur\n`; break;
        default: terrainTxt += `- Tabac : __________\n`; break;
    }
    // Alcool
    let alcoolDetail = formState.terrain.alcool.details ? ` (${_formatVal(formState.terrain.alcool.details, ' consommations/semaine', '_____')})` : '';
    if (formState.terrain.alcool.type.includes('Consommation') && !formState.terrain.alcool.details) alcoolDetail = ' (_____ consommations/semaine)'; // Force placeholder if conso and vide
    switch (formState.terrain.alcool.type) {
        case 'Consommation régulière': terrainTxt += `- Alcool : Consommation régulière${alcoolDetail}\n`; break;
        case 'Consommation occasionnelle': terrainTxt += `- Alcool : Consommation occasionnelle${alcoolDetail}\n`; break;
        case 'Abstinent': terrainTxt += `- Alcool : Abstinent\n`; break;
        case 'Sevrage récent': terrainTxt += `- Alcool : Sevrage récent${alcoolDetail}\n`; break;
         default: terrainTxt += `- Alcool : __________\n`; break;
    }
     // Allergies / Ttt Habituels
    if(formState.terrain.allergies) terrainTxt += `- Allergies : ${formState.terrain.allergies}\n`;
    if(formState.terrain.traitementsHabituels) terrainTxt += `- Traitements Habituels (Autres) : ${formState.terrain.traitementsHabituels}\n`;

    return terrainTxt.trim();
};

const _formatNeuroDetaille = () => {
     if (calculatedGCS.value >= 15) return '';
     const nd = formState.evaluation.neuroDetaille;
     let signesMeninges = [];
     if (nd.nuqueRaide) signesMeninges.push('Raideur nuque');
     if (nd.kernig) signesMeninges.push('Kernig');
     if (nd.brudzinski) signesMeninges.push('Brudzinski');
     let detailTxt = '\n\n Examen Neurologique Détaillé (GCS < 15) :\n';
     detailTxt += `   - Signes Méningés : ${signesMeninges.length > 0 ? signesMeninges.join(', ') : 'Absents'}\n`;
     detailTxt += `   - ROT : ${nd.rots || 'Non évalués'}\n`;
     detailTxt += `   - RCP : ${nd.rcp || 'Non évalué'}\n`;
     if (nd.autresSignes) detailTxt += `   - Autres : ${nd.autresSignes}\n`;
     return detailTxt;
};

const ajusterOrientationSelonSexe = (sexeValue = formState.patient.sexe) => {
    const currentOrientation = formState.evaluation.neuro.orientation;
    if (typeof currentOrientation !== 'string' || currentOrientation === 'Non évaluable') return;
    const genreSuffix = sexeValue === 'féminin' ? 'e' : ''; const reactiveSuffix = sexeValue === 'féminin' ? 've' : '';
    const baseOriented = 'Bien orienté'; const suffixOriented = ' temps et espace'; const baseConfus = 'Confus'; const baseDesoriented = 'Désorienté'; const baseNonReactif = 'Non réactif';
    if (currentOrientation.startsWith(baseOriented)) { formState.evaluation.neuro.orientation = `${baseOriented}${genreSuffix}${suffixOriented}`; }
    else if (currentOrientation.startsWith(baseConfus)) { formState.evaluation.neuro.orientation = `${baseConfus}${genreSuffix}`; }
    else if (currentOrientation.startsWith(baseDesoriented)) { formState.evaluation.neuro.orientation = `${baseDesoriented}${genreSuffix}`; }
    else if (currentOrientation.startsWith(baseNonReactif)) { formState.evaluation.neuro.orientation = `${baseNonReactif}${reactiveSuffix}`; }
};

const generateECGInterpretation = () => {
    const ecg = formState.evaluation.hemo.ecg; // Access reactive state directly
    console.log("Generating ECG Interpretation, Realise:", ecg.realise); // Debug log

    if (!ecg.realise) {
        ecg.interpretation = "ECG non réalisé.";
        if(ecg.rythmeType !== 'Non réalisé') ecg.rythmeType = 'Non réalisé'; // Reset if not done
        return;
    }
    // If marked as realized but no type selected yet, prompt or set default
     if (ecg.rythmeType === 'Non réalisé' || !ecg.rythmeType) {
         ecg.interpretation = "ECG réalisé, veuillez spécifier le rythme.";
         // Optionally default to Sinusal if realised is checked?
         // ecg.rythmeType = 'Sinusal';
         return;
     }


    let interpretation = "";
    const ecgFc = formState.evaluation.hemo.fc ? `, FC : ${_formatVal(formState.evaluation.hemo.fc, ' bpm')}` : ''; // Format heart rate if present

    if (ecg.rythmeType === 'Ininterprétable') { interpretation = "ECG ininterprétable."; }
    else if (ecg.rythmeType === 'Asystolie') { interpretation = "Asystolie."; }
    else if (ecg.rythmeType === 'Fibrillation Ventriculaire') { interpretation = `Fibrillation Ventriculaire (chaotique).`; }
    else {
        interpretation += `${ecg.rythmeType}`;
        if (ecg.rythmeType === 'Fibrillation Atriale') { interpretation += ` (irrégulière)${ecgFc}.`; ecg.regularite = 'Irrégulier'; } // Force irregular
        else if (ecg.rythmeType === 'Flutter Atrial') { interpretation += ` (conduction ${ecg.regularite?.toLowerCase()})${ecgFc}.`; }
        else if (ecg.rythmeType === 'Stimulateur Cardiaque') { interpretation += ` (rythme électro-entraîné, ${ecg.regularite?.toLowerCase()})${ecgFc}.`; }
        else { interpretation += `, ${ecg.regularite?.toLowerCase()}${ecgFc}.`; } // General case

        // Anomalies ST/T
        if (ecg.stAnomalies !== 'Normal' && ecg.stAnomalies !== 'Non évaluable') {
            interpretation += ` Avec ${ecg.stAnomalies}`;
            if (ecg.stLocalisation) { interpretation += ` en ${ecg.stLocalisation}`; }
            interpretation += ".";
        } else if (ecg.stAnomalies === 'Normal') {
            interpretation += " Repolarisation normale.";
        }
    }

    console.log("Assigning interpretation:", interpretation.trim()); // Debug log
    ecg.interpretation = interpretation.trim();
};

// Deep assign utility (simple version, might need library for complex cases)
const deepAssign = (target, source) => {
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const targetValue = target?.[key]; // Use optional chaining
      const sourceValue = source[key];

      if (sourceValue !== null && typeof sourceValue === 'object' && !Array.isArray(sourceValue) &&
          targetValue !== null && typeof targetValue === 'object' && !Array.isArray(targetValue))
      {
        // Ensure target object exists before assigning to its properties
         if (!target[key]) target[key] = {};
         deepAssign(target[key], sourceValue); // Recurse
      } else if (sourceValue !== undefined && key !== 'newAntecedentName') { // Avoid copying undefined and specific fields
            // Assign primitives, replace arrays or objects directly
            // Use JSON parse/stringify for a safe deep copy of arrays/plain objects if needed
            if (Array.isArray(sourceValue)) {
                 target[key] = JSON.parse(JSON.stringify(sourceValue));
            } else {
                 target[key] = sourceValue;
            }
      }
    }
  }
};


const resetForm = () => {
    console.log("Resetting form");
    isEditing.value = false;
    currentDossierId.value = null;
    saveError.value = '';
    const initialData = getInitialFormData();
    deepAssign(formState, initialData); // Use deepAssign to reset the reactive object
    // Explicitly ensure arrays are reset correctly if deepAssign isn't perfect
    formState.terrain.antecedents = initialData.terrain.antecedents;
    formState.evaluation.resp.tirage.type = [];


    nextTick(() => {
         console.log("Form reset, check orientation:", formState.evaluation.neuro.orientation);
         ajusterOrientationSelonSexe(); // Adjust orientation after reset
     });
};

const populateForm = (dossierData) => {
    console.log("Populating form with dossier ID:", dossierData.id);
    const raw = dossierData.raw_data;
    if (!raw) { console.error("raw_data manquant!"); resetForm(); return; }

    // Use the 'sexe' from the loaded data to get the correct initial structure
    const initialForSexe = getInitialFormData(raw.patient?.sexe || 'masculin');
    // Start with the correct initial structure
    deepAssign(formState, initialForSexe);

    // Apply the loaded data over the initial structure
    try {
        deepAssign(formState, raw);

        // Ensure specific fields are handled correctly after merge
        formState.terrain.newAntecedentName = ''; // Always clear this field

        isEditing.value = true; // Set editing mode AFTER populating
        currentDossierId.value = dossierData.id;
        saveError.value = '';

        nextTick(() => {
            // No need to call ajusterOrientation here, as getInitialFormData + deepAssign handled it
            generateECGInterpretation(); // Recalculate based on loaded ECG data
            console.log("Form populated, check patient.nom:", formState.patient.nom);
        });

    } catch (e) {
        console.error("Error during populateForm assignment:", e);
        alert("Erreur lors du chargement des données pour l'édition.");
        resetForm(); // Reset if population fails
    }
};


// --- Computed Properties (Declared AFTER helper functions if they use them) ---
const accordGenre = computed(() => (formState.patient.sexe === 'féminin' ? 'e' : ''));
const calculatedGCS = computed(() => { const { yeux, verbal, moteur } = formState.evaluation.neuro; return (yeux >= 1 && verbal >= 1 && moteur >= 1) ? yeux + verbal + moteur : 0; });
const gcsYeuxText = computed(() => { const map = { 4: 'Spontanée', 3: 'À la demande', 2: 'À la douleur', 1: 'Aucune' }; return map[formState.evaluation.neuro.yeux] || ''; });
const gcsVerbalText = computed(() => { const map = { 5: 'Orientée', 4: 'Confuse', 3: 'Inappropriée', 2: 'Incompréhensible', 1: 'Aucune' }; return map[formState.evaluation.neuro.verbal] || ''; });
const gcsMoteurText = computed(() => { const map = { 6: 'Obéit', 5: 'Localise', 4: 'Retrait', 3: 'Flexion Anormale', 2: 'Extension Anormale', 1: 'Aucune' }; return map[formState.evaluation.neuro.moteur] || ''; });
const calculateHeartScore = () => {
    const h = formState.evaluation.scores.heart;
    if ([h.history, h.ecg, h.age, h.riskFactors, h.troponin].some(v => v === null || v === undefined)) {
        return null; // Not all components filled
    }
    // Ensure values are numbers before summing
    const score = Number(h.history || 0) + Number(h.ecg || 0) + Number(h.age || 0) + Number(h.riskFactors || 0) + Number(h.troponin || 0);
    formState.evaluation.scores.heart.result = score; // Update reactive state
    return score;
};

const calculateCurb65Score = () => {
    const c = formState.evaluation.scores.curb65;
    // Automatically set age based on patient info
    c.age65 = (formState.patient.age !== null && formState.patient.age >= 65);

    // Simple checks (more complex logic might be needed for Urea/BP thresholds)
    // Let's assume boolean inputs for simplicity for now
    let score = 0;
    if (c.confusion) score++;
    if (c.urea) score++; // Assuming checkbox means Urea > 7 mmol/L or BUN > 19 mg/dL
    if (c.respiratoryRate) score++; // Assuming checkbox means RR >= 30
    if (c.bloodPressure) score++; // Assuming checkbox means SBP < 90 or DBP <= 60
    if (c.age65) score++;

    formState.evaluation.scores.curb65.result = score;
    return score;
};

const calculateQSOFAScore = () => {
    const q = formState.evaluation.scores.qsofa;
     // Auto-set based on other form fields
     q.alteredMentalStatus = calculatedGCS.value < 15; // Use calculatedGCS

    // Simple checks (requires inputs to be checkboxes indicating criteria met)
    let score = 0;
    if (q.respiratoryRate) score++; // Assuming checkbox means RR >= 22
    if (q.alteredMentalStatus) score++; // From GCS check
    if (q.systolicBP) score++; // Assuming checkbox means SBP <= 100

    formState.evaluation.scores.qsofa.result = score;
    return score;
};

// Computed properties to display scores
const computedHeartScore = computed(calculateHeartScore);
const computedCurb65Score = computed(calculateCurb65Score);
const computedQSOFAScore = computed(calculateQSOFAScore);

// --- Build Auto Total Sentence (Depends on computed GCS) ---


const _buildAutoTotalSentence = () => {
    const typeDetresse = formState.evaluation.distressIdentifiee;
    let sentence = "Il s'agit "; // Start phrase differently

    if (typeDetresse === 'respiratoire') {
        sentence += `d'une détresse respiratoire`;
        const auscu = formState.evaluation.resp.auscultation;
        if (auscu) sentence += ` avec à l'auscultation pulmonaire : ${auscu}`;
    } else if (typeDetresse === 'hemodynamique') {
        sentence += `d'une détresse hémodynamique`;
        const ecg = formState.evaluation.hemo.ecg;
        if (ecg.realise && ecg.interpretation && ecg.interpretation !== "ECG non réalisé.") {
             sentence += ` (ECG: ${ecg.interpretation})`;
        }
    } else if (typeDetresse === 'neurologique') {
        sentence += `d'une détresse neurologique`;
        const gcs = calculatedGCS.value;
        const glycemie = formState.evaluation.examenComp.glycemie;
        sentence += ` (GCS à ${gcs}/15`;
        if (glycemie) sentence += `, glycémie capillaire à ${glycemie}`;
        sentence += ")";
    } else { // 'aucune'
        sentence = "Il n'y a pas de détresse vitale actuellement"; // Changed wording
    }
    sentence += ".";
    // Ajoute la synthèse manuelle de l'utilisateur si elle existe
    if(formState.synthese) {
        sentence += ` ${formState.synthese}`;
    }
    return sentence;
};
const _buildFinalSummarySentence = () => {
    const { patient, terrain, motifConsultation, evaluation } = formState;
    const nom = patient.nom || 'X'; const age = patient.age || 'N/A';
    const atcds = terrain.antecedents.filter(a => a.present).map(a => a.name.split('/')[0].trim()).slice(0, 3).join(', ');
    const motif = motifConsultation || 'motif non spécifié';
    let etat = ''; const typeDetresse = evaluation.distressIdentifiee;
    if (typeDetresse !== 'aucune') {
         etat = `présentant une détresse ${typeDetresse}`;
         if (typeDetresse === 'respiratoire' && evaluation.resp.auscultation) etat += ` (${evaluation.resp.auscultation})`;
         else if (typeDetresse === 'neurologique') etat += ` (GCS ${calculatedGCS.value}/15)`;
         // Add hemo detail if needed: else if (typeDetresse === 'hemodynamique' && evaluation.hemo.ta) etat += ` (TA ${evaluation.hemo.ta})`
    } else {
        etat = 'sans détresse vitale évidente actuellement';
    }
    return `Au total, il s'agit du patient ${nom} âgé de ${age} ans, ${atcds ? 'aux antécédents de ' + atcds + ', ' : ''}qui consulte pour ${motif}, ${etat}.`;
};


// --- Watcher (Declared AFTER functions it calls) ---
watch(() => props.editingDossier, (newDossier) => {
    console.log("Watcher for editingDossier triggered", newDossier);
    if (newDossier && newDossier.id && newDossier.raw_data) {
        populateForm(newDossier);
    } else if (!newDossier && isEditing.value) {
        resetForm();
    }
}, { immediate: true, deep: true });
watch(() => formState.patient.age, calculateCurb65Score);

watch(() => ({
    realise: formState.evaluation.hemo.ecg.realise,
    rythmeType: formState.evaluation.hemo.ecg.rythmeType,
    regularite: formState.evaluation.hemo.ecg.regularite,
    fc: formState.evaluation.hemo.ecg.fc, // Watch new field
    stAnomalies: formState.evaluation.hemo.ecg.stAnomalies,
    stLocalisation: formState.evaluation.hemo.ecg.stLocalisation
}), (newVal, oldVal) => {
    console.log("ECG Watch triggered:", newVal); // Debug log
    generateECGInterpretation(); // Call the generation function
}, { deep: true }); // deep: true is important here if watching an object/array structure
// --- Other Methods ---
const setMotif = (motif) => { formState.motifConsultation = motif; };
const addAntecedent = () => {
    const name = formState.terrain.newAntecedentName.trim();
    if (name && !formState.terrain.antecedents.some(atcd => atcd.name.toLowerCase() === name.toLowerCase())) {
        formState.terrain.antecedents.push({ id: Date.now().toString(), name: name, present: true, details: '' });
        formState.terrain.newAntecedentName = '';
    } else if (name) { alert("Cet antécédent existe déjà."); }
};
const copyDossierToClipboard = () => {
        const textToCopy = generateDossier();
        navigator.clipboard.writeText(textToCopy).then(() => {
            alert('Dossier copié dans le presse-papiers !');
        }).catch(err => {
            console.error('Erreur lors de la copie : ', err);
            alert('Erreur lors de la copie du dossier.');
        });
    };
const triggerSaveOrUpdateDossier = async () => {
    saveLoading.value = true; saveError.value = '';
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { saveError.value = "Connexion requise."; saveLoading.value = false; return; }

    const dossierText = generateDossier();
    // Use current formState for rawData, ensure deep copy
    const rawData = JSON.parse(JSON.stringify(formState));

    if (isEditing.value && currentDossierId.value) { // UPDATE
        const updatePayload = { patient_nom: formState.patient.nom, patient_age: formState.patient.age, motif_consultation: formState.motifConsultation, dossier_text: dossierText, raw_data: rawData, };
        console.log("Attempting UPDATE for ID:", currentDossierId.value);
        try {
            const { data, error } = await supabase.functions.invoke(`update-dossier/${currentDossierId.value}`, { method: 'PATCH', body: updatePayload });
            if (error) throw error;
            alert('Dossier mis à jour !'); emit('dossier-updated', data);
        } catch (error) { console.error("Error updating:", error); let msg = error.message; if (error.context?.json?.error) msg = error.context.json.error; saveError.value = `Erreur MàJ: ${msg}`; }
        finally { saveLoading.value = false; }
    } else { // INSERT
        const insertPayload = { patient_nom: formState.patient.nom, patient_age: formState.patient.age, motif_consultation: formState.motifConsultation, dossier_text: dossierText, raw_data: rawData, timestamp: new Date().toISOString() };
        console.log("Attempting INSERT");
        try {
            const { data, error } = await supabase.functions.invoke('save-dossier', { body: insertPayload });
            if (error) throw error;
            alert('Dossier enregistré !'); emit('dossier-saved', data); resetForm();
        } catch (error) { console.error("Error saving:", error); let msg = error.message; if (error.context?.json?.error) msg = error.context.json.error; saveError.value = `Erreur Enregistrement: ${msg}`; }
        finally { saveLoading.value = false; }
    }
};
const cancelEdit = () => { resetForm(); emit('cancel-edit'); };
const generateDossier = () => {
  // Destructure for easier access within this function
  const { patient, motifConsultation, evaluation, synthese, conduite, terrain, histoire } = formState;
  const { resp, hemo, neuro, neuroDetaille, examenComp } = evaluation;

  // Use helper functions which now correctly read from formState
  const patientNom = _formatVal(patient.nom);
  const patientAge = _formatVal(patient.age);
  const motifConsultationVal = _formatVal(motifConsultation, '', 'Motif non spécifié');

  const voiesAeriennes = _formatVal(resp.voies);
  const spo2 = _formatVal(resp.spo2);
  const freqResp = _formatVal(resp.fr);
  const auscultation = resp.auscultation || '__________';
  const signesLutte = _formatSignesLutte(); // Gets detailed string
  const cyanose = resp.cyanose === 'Oui' ? 'Oui' : 'Non';

  const extremites = _formatVal(hemo.extremites);
  const tensionArt = _formatVal(hemo.ta, ' mmHg');
  const poulsFC = _formatVal(hemo.fc);
  const poulsReg = hemo.rythmePalpe.toLowerCase();
  const poulsPeriph = _formatVal(hemo.pouls);
  // Corrected logic: checkbox TRUE = sign PRESENT -> output "Signe Présent"
  // checkbox FALSE = sign ABSENT -> output "Pas de Signe"
  const turgescenceJug = _formatPresenceAbsence(hemo.signesICD.turgescenceJug, 'turgescence des veines jugulaires', 'Pas de ');
  const refluxHepato = _formatPresenceAbsence(hemo.signesICD.refluxHepatoJug, 'reflux hépatojugulaire', 'Pas de ');
  const oedemesMI = _formatPresenceAbsence(hemo.signesICD.oedemesMI, 'OMI Bilatéraux', 'Pas d\'');

  const gcs = calculatedGCS.value;
  const gcsY = _formatVal(neuro.yeux, '', ' ');
  const gcsV = _formatVal(neuro.verbal, '', ' ');
  const gcsM = _formatVal(neuro.moteur, '', ' ');
  let orientation = _formatVal(neuro.orientation);
    // Keep detailed orientation from form state
    // if (orientation.includes('Bien orienté')) orientation = 'Bien orienté(e)';
    // else if (orientation.includes('Confus')) orientation = 'Confus(e)';
    // else if (orientation.includes('Non réactif')) orientation = 'Non réactif(ve)';
    // else if (orientation.includes('Désorienté')) orientation = 'Désorienté(e)';
    // else orientation = '__________';
  const pupilles = neuro.pupilles || '__________';
  const deficitsNeuro = _formatNeuroDeficits(); // Gets detailed string

  const totalTxt = _buildAutoTotalSentence(); // Uses specific distress info + user synthesis
  const conduiteTxt = _formatConduite(); // Detailed list
  const terrainTxt = _formatTerrain(); // Detailed list
  const histoireTxt = histoire || "__________";

  const temp = _formatVal(examenComp.temp, ' °C');
  const glycemie = _formatVal(examenComp.glycemie);
   // Corrected logic: checkbox TRUE = sign PRESENT -> output "Signes présents"
   // checkbox FALSE = sign ABSENT -> output "Pas de signes"
  const deshydratation = _formatPresenceAbsence(examenComp.signesDeshydratation, 'signes de déshydratation', 'Pas de ');
  const abdomen = examenComp.abdomen || '__________';
  const tr = examenComp.tr || 'Non réalisé'; // Changed default
  const { scores } = formState.evaluation;
  let scoresText = "\n\nScores Cliniques:\n"; // Add extra newline
  let scoresAdded = false;

  // Reference the computed properties to ensure they are calculated
  const heartResult = computedHeartScore.value;
  const curbResult = computedCurb65Score.value;
  const qsofaResult = computedQSOFAScore.value;

  if (heartResult !== null) { scoresText += `   - HEART Score: ${heartResult}\n`; scoresAdded = true; }
  if (curbResult !== null) { scoresText += `   - CURB-65 Score: ${curbResult}\n`; scoresAdded = true; }
  if (qsofaResult !== null) { scoresText += `   - qSOFA Score: ${qsofaResult}\n`; scoresAdded = true; }
  const finalSummary = _buildFinalSummarySentence(); // The very brief summary
  

  // --- Assemble final text using the desired detailed structure ---
  return `
Il s'agit du patient ${patientNom} agé de ${patientAge} ans.

Consulte pour ${motifConsultationVal}.

Évaluation de la gravité:
 *. Respiratoire :
   - Voies aériennes : ${voiesAeriennes}
   - SpO2 : ${spo2} %
   - Fréquence respiratoire : ${freqResp} cycle/min
   - Auscultation pulmonaire : ${auscultation}
   ${signesLutte}
   - Cyanose : ${cyanose}

 *. Hémodynamique :
   - Extrémités : ${extremites}
   - Tension artérielle (TA) : ${tensionArt}
   - Pouls : ${poulsFC} bpm (${poulsReg})
   - Pouls périphériques : ${poulsPeriph}
   - ${turgescenceJug}
   - ${refluxHepato}
   - ${oedemesMI}
 ECG : ${hemo.ecg.realise ? hemo.ecg.interpretation : 'Non réalisé'}
   
 *. Neurologique :
   - Score de Glasgow : ${gcs}/15
     - Ouverture des yeux : ${gcsY}/4 (${gcsYeuxText.value || ''})
     - Réponse verbale : ${gcsV}/5 (${gcsVerbalText.value || ''})
     - Réponse motrice : ${gcsM}/6 (${gcsMoteurText.value || ''})
   - Orientation : ${orientation}
   - Réflexes pupillaires : Pupilles : ${pupilles}
   - ${deficitsNeuro}
    ${calculatedGCS.value < 15 ? _formatNeuroDetaille() : ''}

                               Au total
${totalTxt}

 Conduite immédiate :
${conduiteTxt}

 Terrain :
${terrainTxt}

 Histoire de la maladie :
${histoireTxt}

---
 *. Reste de l'examen :
   - Température corporelle : ${temp}
   - Glycémie capillaire : ${glycemie}
   - ${deshydratation}
   - Examen abdominal : ${abdomen}
   - TR : ${tr}

---
${scoresAdded ? scoresText.trim() : ''}

Au total :
${finalSummary}
      `.trim(); // Use trim() only at the end
};

// --- Lifecycle ---
onMounted(() => {
    // Watch immediate:true handles initial population
    console.log("DossierForm mounted. Initial isEditing:", isEditing.value);
    if (!isEditing.value) { // Apply defaults if not editing initially
         ajusterOrientationSelonSexe();
         generateECGInterpretation();
    }
});

</script>
<style scoped>  
 .card {
    background-color: var(--component-background) !important;
    border-color: var(--border-color-light) !important;
    color: var(--text-color) !important;
  }
  .card-header {
    background-color: var(--component-background-alt) !important;
    border-bottom-color: var(--border-color-light) !important;
    color: var(--text-color) !important; /* Ensure header text is visible */
  }
   .card-footer {
     background-color: var(--component-background-alt) !important;
     border-top-color: var(--border-color-light) !important;
  }
  .card-title {
    color: var(--text-color) !important; /* Ensure card titles are readable */
  }

  /* Form Checks (Radio/Checkbox) */
  .form-check-label {
     color: var(--text-color) !important; /* Make labels light */
  }
  .form-check-input:disabled ~ .form-check-label {
      color: var(--text-color-muted) !important; /* Style disabled labels */
      opacity: 0.7;
  }
   /* Checkbox background when checked */
   .form-check-input:checked {
       background-color: var(--primary-color);
       border-color: var(--primary-color);
   }
   /* Radio button background */
   .form-check-input[type="radio"] {
        background-color: var(--component-background); /* Keep radio bg lightish */
   }
   .form-check-input[type="radio"]:checked {
       background-color: var(--primary-color);
       border-color: var(--primary-color);
       /* Style the inner dot */
       background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='2' fill='%23fff'/%3e%3c/svg%3e");
   }


  /* Form Select */
  .form-select {
    background-color: var(--component-background-alt) !important;
    color: var(--text-color) !important;
    border-color: var(--border-color) !important;
     /* Style the dropdown arrow */
     background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23dee2e6' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e") !important;
  }
  .form-select:focus {
      border-color: var(--primary-color) !important;
      box-shadow: 0 0 0 0.2rem rgba(var(--primary-color-rgb), 0.35) !important;
  }

  /* Borders */
  .border-top {
     border-top-color: var(--border-color-light) !important;
  }
  .border {
     border-color: var(--border-color-light) !important;
  }

   /* Specific overrides for nested sections if needed */
   .nested-details {
       background-color: var(--component-background-alt) !important;
       border-color: var(--border-color) !important;
   }
   .detailed-neuro-exam {
       background-color: rgba(var(--primary-color-rgb), 0.1) !important; /* Subtle primary tint */
       border-color: rgba(var(--primary-color-rgb), 0.3) !important;
   }
</style>
